import type { FeedIssue } from "./feed_issues";
import type { ViewKey, ViewRow } from "./order_views";
import type { RowBands } from "./signal";
import type { StrategyBlock } from "./strategies";

import { call, DEFAULT_BASIS, PricingError, type Basis, type Channel, type Component, type Flag, type Market, type MissingReason, type RowChange } from "./api";
import { COPY } from "./format";
import { stageTarget } from "./price";
import type { GridRow } from "./api";

// The projection at a candidate price (C2 v1.13 `POST /api/price-run/project`, CL-020, gate H9). People only, the area
// view. The browser sends the typed price and shows what C2 answers: every money and percentage figure is C2's string,
// the module computes nothing (C1 H12). Nothing is stored by the route, so the read is safe to repeat on every typed price.

/** A missing input the projection names (C1 `MissingReason` plus the two registry rates only the projection reads). */
export type ProjectionMissing = MissingReason | "no_referral_rate" | "no_dsf_rate";
export type ProjectedMargins = {
  gross_pct: string | null; fulfilment_pct: string | null; cogs_fulfilment_pct: string | null; fees_pct: string | null;
  /** C2 serves the TRUE Contribution figures on the projection (C2 v1.66 onward); absent on an older API. */
  true_contribution_pct?: string | null; true_contribution_missing?: string[];
  true_contribution_ex_ads_pct?: string | null; true_contribution_ex_ads_missing?: string[];
};
/** One fee at the candidate price: recomputed from the fee-rate registry (referral, dsf) or fixed as PFC fed it. `standard_value`
 * is PFC's standard at the committed price, shown beside a recompute so the two can be compared. */
export type ProjectedComponent = {
  component: Component; value: string | null; currency: string | null; method: "recomputed" | "fixed"; source: "fee_registry" | "pfc_standard";
  flag: Flag | "RECOMPUTED"; standard_value: string | null; rate?: string | null; minimum?: string | null; minimum_applied?: boolean;
  registry_effective_from?: string | null; registry_version?: number | null; fed_on?: string | null;
};
export type ProjectedLine = {
  grain: "marketplace" | "ship_country"; location: string | null; vat_rate: string | null; components: ProjectedComponent[];
  net_price: string | null; fulfilment_total: string | null; margins: ProjectedMargins; orders: number | null; missing: ProjectionMissing[];
  /** C2 v1.78: the line's bands (contribution, contribution_ex_ads, fulfilment, gross); absent on an older API. */
  bands?: RowBands;
};
/** The same figures at the committed price through the same arithmetic, so the two compare like with like; null when no price is committed. */
export type ProjectedCurrent = { price: string; net_price: string | null; fulfilment_total: string | null; margins: ProjectedMargins; components: ProjectedComponent[] | null };
export type VsPrevious = { value: string | null; previous_price: string | null; reason: string | null };
export type VsAmazonUk = {
  value: string | null; anchor_sku: string | null; anchor_price: string | null; anchor_currency: string; compared_price: string | null;
  fx_rate: string | null; fx_rate_date: string | null; reason: string | null;
  /** C2 v1.14 D6: the anchor is the Amazon UK price saved in the open UK run, not the committed one (absent before v1.14). */
  anchor_pending?: boolean;
};
export type VsRrp = {
  value: string | null; rrp: string | null; rrp_currency: string | null; anchor_market: string | null; comparable: boolean | null;
  fx_rate: string | null; fx_rate_date: string | null; compared_price: string | null; below: boolean | null; reason: string | null;
};
/** C2 C7: the price against the highest competitor price; `value` is a signed percentage string, null with a reason. */
export type VsCompetitor = { value: string | null; reference: string | null; observed_at: string | null; competitor_count: number | null; reason: string | null };
export type ProjectionWeighting = { method: "orders" | "plain_mean" | null; countries_counted: number; countries_total: number; orders_counted: number; orders_total: number };
/** C2 v1.14: the assigned strategy's verdicts at the candidate price ride with the figures (absent on an older API). */
export type Projection = {
  strategy?: StrategyBlock;
  xero_sku: string; product: string; variant: string; channel: Channel; channel_location: string; market: Market; marketplace_id: string;
  marketplace_name: string | null; amazon_sku: string | null; currency: string; as_of: string; cogs_basis: Basis; bases_available: string[];
  price: { value: string; currency: string; vat_inclusive: boolean }; current_price: { value: string; currency: string } | null;
  cogs: { value: string; currency: string; native_value: string; native_currency: string; effective_date: string; flag: Flag } | null;
  net_price: string | null; fulfilment_total: string | null; margins: ProjectedMargins; weighting: ProjectionWeighting | null;
  lines: ProjectedLine[]; current: ProjectedCurrent | null;
  metrics: { vs_previous_price_pct: VsPrevious; vs_amazon_uk_pct: VsAmazonUk; vs_rrp_pct: VsRrp; vs_competitor_max_pct?: VsCompetitor };
  missing: ProjectionMissing[]; method: string; notes: string[];
  /** C2 v1.78 (c2-project-row-preview): the band of each cell at this price; absent on an older API. */
  bands?: RowBands;
  /** C2 v1.78: the order-type view's row at this list price, only when the body named `view`. */
  view_row?: ViewRow;
  /** C2 v1.26: on a picked date, the fees that differ from today's (a commission or DSF change carries the registry rate as a
   * fraction, every other component an amount); empty on today, absent on an older API. */
  changes?: RowChange[];
  /** C2 v1.56 (C1 v1.60, GATE-1089): the open issues of the feeds behind this projection; absent on an older API. */
  feed_issues?: FeedIssue[];
};
export type ProjectBody = { xero_sku: string; marketplace_id?: string; new_price: string; cogs_basis?: Basis; as_of?: string; view?: ViewKey };

export const projectPrice = (body: ProjectBody) => call<Projection>("/price-run/project", { method: "POST", body: JSON.stringify(body) });

/** The body for a row at a price on a basis: the stage target (an Amazon row names its marketplace, a store or ship-country row
 * the store's SKU), the price as typed and cleaned, the basis only when it is not the default (as `getGrid` asks), and the
 * picked date only when one is picked (C2 v1.26: the fees in force on it; absent means today, as before). */
export function projectionBody(row: GridRow, price: string, basis: Basis = DEFAULT_BASIS, asOf: string | null = null): ProjectBody {
  return { ...stageTarget(row), new_price: price, ...(basis === DEFAULT_BASIS ? {} : { cogs_basis: basis }), ...(asOf === null ? {} : { as_of: asOf }) };
}

export const PROJECTION_KEY = ["pricing", "project"] as const;
/** One projection per SKU, place, basis and price, and per date when one is picked (today's key is unchanged). Never stale and never retried: a refusal (a bad price, an inactive SKU)
 * is an answer, not a fault, and the committed price it compares with changes at every commit (the bar invalidates the key). */
export function projectionQuery(body: ProjectBody) {
  return {
    queryKey: [...PROJECTION_KEY, body.xero_sku, body.marketplace_id ?? "ALL", body.cogs_basis ?? DEFAULT_BASIS, body.new_price, ...(body.as_of ? [body.as_of] : []), ...(body.view ? ["view", body.view] : [])] as const,
    queryFn: () => projectPrice(body), retry: 0, staleTime: 0,
  };
}

/** The sentence for a projection the API refused or could not give. A refusal of the typed price reads as the box's own
 * price sentence; anything else says the margins cannot be worked out right now, never a field name. */
export function projectionRefusal(error: unknown): string {
  if (error instanceof PricingError) {
    const reason = error.reason();
    if (reason === "bad_price" || reason === "bad_request") return COPY.enterPrice;
    if (reason === "inactive_sku" || reason === "not_priced_here" || reason === "wrong_marketplace" || reason === "unknown_sku") return COPY.projNotPricedHere;
  }
  return COPY.projUnavailable;
}
